import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  PrimaryButton,
  StyledCard,
  PageContainer,
  SectionHeader,
} from '../components/ui/StyledComponents'
import BackButton from '../components/ui/BackButton'
import dayjs from 'dayjs'
import { getSubstituteAssignments } from '../utils/substituteUtils'
import { useAuth } from '../context/AuthContext'
import SubstituteAssignmentForm from '../components/admin/SubstituteAssignmentForm'
import supabase from '../supabaseClient'

function ClassDetailPage() {
  const { classId } = useParams()
  const navigate = useNavigate()
  const { userRole } = useAuth()
  const [classData, setClassData] = useState(null)
  const [students, setStudents] = useState([])
  const [substituteAssignments, setSubstituteAssignments] = useState([])
  const [showSubstituteForm, setShowSubstituteForm] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  // Fetch the class data based on classId
  useEffect(() => {
    const fetchClassData = async () => {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('id', classId)
        .maybeSingle()

      if (error) {
        console.error('Error fetching class data:', error)
      } else {
        setClassData(data)
      }
    }

    fetchClassData()
  }, [classId])

  // Fetch the list of students in the class
  useEffect(() => {
    const fetchStudents = async () => {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .contains('class_ids', [classId])
        .eq('is_active', true)

      if (error) {
        console.error('Error fetching students:', error)
      } else {
        setStudents(data)
      }
    }

    fetchStudents()
  }, [classId])

  // Fetch substitute assignments for this class
  useEffect(() => {
    const fetchSubstituteAssignments = async () => {
      if (!classId) return
      
      try {
        // Get assignments for the last 30 days and next 90 days
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - 30)
        const endDate = new Date()
        endDate.setDate(endDate.getDate() + 90)
        
        const assignments = await getSubstituteAssignments(
          dayjs(startDate).format('YYYY-MM-DD'),
          dayjs(endDate).format('YYYY-MM-DD'),
          { classId }
        )
        setSubstituteAssignments(assignments)
      } catch (error) {
        console.error('Error fetching substitute assignments:', error)
      }
    }

    fetchSubstituteAssignments()
  }, [classId])

  // Navigate to the AttendancePage for a specific student
  const handleAttendanceClick = (studentId) => {
    navigate(`/student_details/${studentId}`)
  }

  const handleCreateSubstituteAssignment = () => {
    setShowSubstituteForm(true)
  }

  const handleSubstituteFormClose = () => {
    setShowSubstituteForm(false)
  }

  const handleSubstituteFormSuccess = () => {
    // Refresh substitute assignments
    const fetchSubstituteAssignments = async () => {
      try {
        const startDate = new Date()
        startDate.setDate(startDate.getDate() - 30)
        const endDate = new Date()
        endDate.setDate(endDate.getDate() + 90)
        
        const assignments = await getSubstituteAssignments(
          dayjs(startDate).format('YYYY-MM-DD'),
          dayjs(endDate).format('YYYY-MM-DD'),
          { classId }
        )
        setSubstituteAssignments(assignments)
      } catch (error) {
        console.error('Error fetching substitute assignments:', error)
      }
    }
    fetchSubstituteAssignments()
    setShowSubstituteForm(false)
  }

  const getAssignmentStatus = (assignment) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'warning' },
      completed: { label: 'Completed', color: 'success' },
      cancelled: { label: 'Cancelled', color: 'error' }
    }
    
    return statusConfig[assignment.status] || { label: 'Unknown', color: 'default' }
  }

  return (
    <PageContainer>
      {/* Back Button */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <BackButton label="Back To Classes" />
      </Box>

      {classData ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Class Info */}
          <Grid item xs={12}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Class Details
              </SectionHeader>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                <strong>Class Name:</strong> {classData.name}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                <strong>Description:</strong>{' '}
                {classData.description || 'No description available'}
              </Typography>
            </StyledCard>
          </Grid>

          {/* Substitute Assignments */}
          {isAdmin && (
            <Grid item xs={12}>
              <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  mb: { xs: 1.5, sm: 2 },
                  gap: { xs: 1.5, sm: 0 },
                }}>
                  <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 0 }}>
                    Substitute Assignments
                  </SectionHeader>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateSubstituteAssignment}
                    fullWidth={isMobile}
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      padding: { xs: '10px 16px', sm: '12px 24px' },
                    }}
                  >
                    Create Assignment
                  </Button>
                </Box>
                
                {substituteAssignments.length > 0 ? (
                  <TableContainer
                    sx={{
                      overflowX: { xs: 'auto', sm: 'visible' },
                      '&::-webkit-scrollbar': {
                        height: { xs: '6px', sm: '0px' },
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '3px',
                      },
                    }}
                  >
                    <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                            Original Teacher
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>
                            Substitute Teacher
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                            Period
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                            Reason
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {substituteAssignments.map((assignment) => {
                          const status = getAssignmentStatus(assignment)
                          return (
                            <TableRow key={assignment.id}>
                              <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    {assignment.original_teacher?.first_name} {assignment.original_teacher?.last_name}
                                  </Typography>
                                  {isMobile && (
                                    <>
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                        Substitute: {assignment.substitute_teacher?.first_name} {assignment.substitute_teacher?.last_name}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                                        Reason: {assignment.reason}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                                {assignment.substitute_teacher?.first_name} {assignment.substitute_teacher?.last_name}
                              </TableCell>
                              <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    {dayjs(assignment.start_date).format('MMM D')} - {dayjs(assignment.end_date).format('MMM D, YYYY')}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    {Math.ceil((new Date(assignment.end_date) - new Date(assignment.start_date)) / (1000 * 60 * 60 * 24)) + 1} days
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, display: { xs: 'none', lg: 'table-cell' } }}>
                                {assignment.reason}
                              </TableCell>
                              <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                <Chip
                                  label={status.label}
                                  color={status.color}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    height: { xs: 20, sm: 24 },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography 
                    color="textSecondary"
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    No substitute assignments found for this class.
                  </Typography>
                )}
              </StyledCard>
            </Grid>
          )}

          {/* Students Table */}
          <Grid item xs={12}>
            <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: { xs: 1.5, sm: 2 } }}>
                Students
              </SectionHeader>
              <TableContainer
                sx={{
                  overflowX: { xs: 'auto', sm: 'visible' },
                  '&::-webkit-scrollbar': {
                    height: { xs: '6px', sm: '0px' },
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                  },
                }}
              >
                <Table sx={{ minWidth: { xs: 400, sm: 'auto' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap', width: { xs: '40px', sm: '50px' } }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Student Name
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, whiteSpace: 'nowrap' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length > 0 ? (
                      students.map((student, index) => (
                        <TableRow key={student.id}>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {student.first_name} {student.last_name}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            <PrimaryButton
                              variant="contained"
                              onClick={() => handleAttendanceClick(student.id)}
                              fullWidth={isMobile}
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                padding: { xs: '8px 12px', sm: '10px 16px' },
                                minWidth: { xs: 'auto', sm: '150px' },
                              }}
                            >
                              {isMobile ? 'View' : 'View Student Details'}
                            </PrimaryButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell 
                          colSpan={3} 
                          align="center"
                          sx={{ 
                            py: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          }}
                        >
                          No students in this class
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          </Grid>
        </Grid>
      ) : (
        <StyledCard sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Typography 
            variant="body1"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Loading class data...
          </Typography>
        </StyledCard>
      )}

      {/* Substitute Assignment Form */}
      <SubstituteAssignmentForm
        open={showSubstituteForm}
        onClose={handleSubstituteFormClose}
        onSuccess={handleSubstituteFormSuccess}
        classId={classId}
      />
    </PageContainer>
  )
}

export default ClassDetailPage
